import { Component, Input, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { Chargepoint, ChargepointStatus } from '../../../api/chargepoints-api.service';
import { Observable, switchMap, filter, map, tap, catchError, of, Subject, combineLatest, ReplaySubject, debounceTime } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { JsonViewerService } from '../../common/json-viewer-dialog/json-viewer.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RefreshService } from '../../common/refresh-widget/refresh.service';
import { ChargepointsOcppApiService, OcppSession } from '../../../api/chargepoints-ocpp-api.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';

export type SessionTimeRange = '1d' | '1w' | '2w' | '1m' | '2m' | '6m';

export const SESSION_TIME_RANGE_OPTIONS: { value: SessionTimeRange; label: string }[] = [
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
  { value: '2w', label: '2 Weeks' },
  { value: '1m', label: '1 Month' },
  { value: '2m', label: '2 Months' },
  { value: '6m', label: '6 Months' },
];

export interface ShownSession extends OcppSession {
  closeTime: Date;
  historyWidthPercentage: number;
  historyWidthPercentageString: string;
  isGap?: boolean;
  status?: 'current' | 'critical' | 'warning' | 'ok' | 'unknown';
  unknownCloseTime?: boolean;
  durationInSeconds: number;
}

@Component({
  selector: 'app-sessions-history',
  templateUrl: './sessions-history.component.html',
  styleUrls: ['./sessions-history.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatCardModule,
    MatDividerModule,
    MatTableModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatSelectModule,
  ]
})
export class SessionsHistoryComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(['(max-width: 599px)']).pipe(
      map((result) => result.matches)
    ),
    { initialValue: false }
  );

  readonly timeRangeOptions = SESSION_TIME_RANGE_OPTIONS;

  @Input() chargepoint!: Chargepoint;

  public sessions$: Observable<ShownSession[]>;
  public shownSessions$: Observable<ShownSession[]>;
  public uptimePercentage$: Observable<number>;
  public showFrom$: Subject<Date> = new ReplaySubject<Date>();
  public showTo$: Subject<Date> = new ReplaySubject<Date>();
  public loading = false;
  public fetchingError = false;
  public timeRange: SessionTimeRange = '1w';
  public periodDurationSeconds = 60 * 60 * 24 * 7; // 1 weeks

  constructor(private chargepointsOcppApiService: ChargepointsOcppApiService, public jsonViewerService: JsonViewerService, private snackBar: MatSnackBar, private refreshService: RefreshService) {
    const now = new Date();
    this.showTo$.next(now);
    this.showFrom$.next(new Date(now.getTime() - this.periodDurationSeconds * 1000));
    
    this.sessions$ = combineLatest([this.refreshService.refreshTrigger, this.showFrom$, this.showTo$]).pipe(
      filter(() => !!this.chargepoint),
      tap(() => {
        this.loading = true;
        this.fetchingError = false;
      }),
      switchMap(([_refresh, showFrom, showTo]) => this.chargepointsOcppApiService.getChargepointSessions(this.chargepoint.id, showFrom, showTo)),
      tap(() => {
        this.loading = false;
        this.fetchingError = false;
      }),
      catchError(() => {
        this.loading = false;
        this.fetchingError = true;
        return of(null);
      }),
      filter(sessions => sessions !== null),
      map(sessions => sessions.sort((a, b) => a.creationTime.getTime() - b.creationTime.getTime())),
      map(sessions => {
        return sessions.map((session, index) => {
          if (!session.closeTime && index < sessions.length - 1) {
            return {
              ...session,
              closeTime: sessions[index + 1].creationTime,
              unknownCloseTime: true,
            };
          }
          return session;
        });
      }),
      map(sessions => {
        return sessions.map((session: OcppSession & { unknownCloseTime?: boolean }) => {
          const duration = (session.closeTime ?? new Date()).getTime() - session.creationTime.getTime();
          const durationInSeconds = duration / 1000;
          const historyWidthPercentage = durationInSeconds / this.periodDurationSeconds;
          const historyWidthPercentageString = `${historyWidthPercentage * 100}%`;
          let status =  'ok';
          if (!session.closed && !session.closeTime) {
            status = 'current';
          } else if (durationInSeconds < 4 * 60 * 60) {
            status = 'warning';
          } else if (durationInSeconds < 30 * 60) {
            status = 'critical';
          }
          if (session.unknownCloseTime) {
            status = 'unknown';
          }
          return {
            ...session,
            durationInSeconds,
            historyWidthPercentage,
            historyWidthPercentageString,
            status,
          };
        }) as ShownSession[];
      }),
    );

    this.shownSessions$ = combineLatest([this.sessions$, this.showFrom$, this.showTo$]).pipe(
      debounceTime(100),
      map(([sessions, showFrom, showTo]) => {
        
        const result: ShownSession[] = [];

        if (sessions.length > 0 && sessions[0].creationTime.getTime() > showFrom.getTime()) {
          const gapDuration = sessions[0].creationTime.getTime() - showFrom.getTime();
          const gapDurationInSeconds = gapDuration / 1000;
          const historyWidthPercentage = gapDurationInSeconds / this.periodDurationSeconds;
          result.push({
            creationTime: showFrom,
            closeTime: sessions[0].creationTime,
            durationInSeconds: gapDurationInSeconds,
            historyWidthPercentage,
            historyWidthPercentageString: `${historyWidthPercentage * 100}%`,
            isGap: true,
            sessionId: '',
            chargePointId: '',
            closed: false,
          } as ShownSession);
        }

        for (let i = 0; i < sessions.length; i++) {
          const currentSession = sessions[i];
          result.push(currentSession);

          // If there's a next session, check for gap
          if (i < sessions.length - 1) {
            const nextSession = sessions[i + 1];
            const gapStart = currentSession.closeTime!;
            const gapEnd = nextSession.creationTime;
            
            if (gapEnd.getTime() - gapStart.getTime() > 0) {
              // Create a gap session
              const gapDuration = gapEnd.getTime() - gapStart.getTime();
              const gapDurationInSeconds = gapDuration / 1000;
              const historyWidthPercentage = gapDurationInSeconds / this.periodDurationSeconds;
              result.push({
                creationTime: gapStart,
                closeTime: gapEnd,
                durationInSeconds: gapDurationInSeconds,
                historyWidthPercentage,
                historyWidthPercentageString: `${historyWidthPercentage * 100}%`,
                isGap: true,
                sessionId: '',
                chargePointId: '',
                closed: false,
              } as ShownSession);
            }
          }
        }

        if (sessions.length > 0 && sessions[sessions.length - 1].closeTime?.getTime() < showTo.getTime()) {
          const gapDuration = showTo.getTime() - sessions[sessions.length - 1].closeTime!.getTime();
          const gapDurationInSeconds = gapDuration / 1000;
          const historyWidthPercentage = gapDurationInSeconds / this.periodDurationSeconds;
          result.push({
            creationTime: sessions[sessions.length - 1].closeTime!,
            closeTime: showTo,
            durationInSeconds: gapDurationInSeconds,
            historyWidthPercentage,
            historyWidthPercentageString: `${historyWidthPercentage * 100}%`,
            isGap: true,
            sessionId: '',
            chargePointId: '',
            closed: false,
          } as ShownSession);
        }

        return result;
      })
    );

    this.uptimePercentage$ = this.shownSessions$.pipe(  
      map(sessions => {
        const uptime = sessions.filter(session => !session.isGap).reduce((acc, session) => acc + session.durationInSeconds, 0);
        return Math.min(100, 100 * uptime / this.periodDurationSeconds);
      })
    );
  }

  selectTimeRange(timeRange: SessionTimeRange) {
    this.timeRange = timeRange;

    const now = new Date();

    switch (timeRange) {
      case '1d':
        this.periodDurationSeconds = 60 * 60 * 24;
        break;
      case '1w':
        this.periodDurationSeconds = 60 * 60 * 24 * 7;
        break;
      case '2w':
        this.periodDurationSeconds = 60 * 60 * 24 * 14;
        break;
      case '1m':
        this.periodDurationSeconds = 60 * 60 * 24 * 31;
        break;
      case '2m':
        this.periodDurationSeconds = 60 * 60 * 24 * 62;
        break;
      case '6m':
        this.periodDurationSeconds = 60 * 60 * 24 * 183;
        break;
    }
    this.showTo$.next(now);
    this.showFrom$.next(new Date(now.getTime() - this.periodDurationSeconds * 1000));
  }

  public copySessionIdToClipboard(sessionId: string) {
    navigator.clipboard.writeText(sessionId);
    this.snackBar.open('Session ID copied to clipboard', 'Close', { duration: 2000 });  
  }
}